import { registerSW } from 'virtual:pwa-register'

/**
 * Service worker registration.
 *
 * Registered ONLY on admin routes. The public booking pages must never be
 * served from a cache: a diner following a campaign link needs the live
 * availability engine, not last week's app shell.
 *
 * Update behaviour is a PROMPT, not a silent swap. A member of staff halfway
 * through taking a booking must not have the page reload underneath them; they
 * are told an update is ready and choose when to take it.
 *
 * What is cached: the application shell and static assets.
 * What is never cached: every Firebase call — bookings, availability, auth.
 * See the `NetworkOnly` runtime rule in vite.config.ts.
 */

export type UpdateHandler = (applyUpdate: () => void) => void

let updateHandler: UpdateHandler | null = null
let pendingUpdate: (() => void) | null = null

export function onServiceWorkerUpdate(handler: UpdateHandler): void {
  updateHandler = handler
  if (pendingUpdate) {
    handler(pendingUpdate)
    pendingUpdate = null
  }
}

const ADMIN_PREFIXES = ['/app', '/login', '/reset-password', '/signup', '/offline']

export function shouldRegisterServiceWorker(pathname: string): boolean {
  return ADMIN_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  if (!shouldRegisterServiceWorker(window.location.pathname)) return

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      const apply = () => updateSW(true)
      if (updateHandler) updateHandler(apply)
      else pendingUpdate = apply
    },
    onRegisteredSW(_swUrl, registration) {
      // Check hourly so a long-lived tab on a tablet in the pass eventually
      // picks up a release without anybody reloading it.
      if (!registration) return
      window.setInterval(
        () => {
          registration.update().catch(() => undefined)
        },
        60 * 60 * 1000,
      )
    },
    onRegisterError(error) {
      console.warn('Service worker registration failed', error)
    },
  })
}
